import {Injectable} from '@angular/core';
import {createDocumentUrl} from '../graphql/profile.graphql';

@Injectable({
  providedIn: 'root'
})
export class GraphqlRouting {

  private adminRouting = [
    "retrieveCompanySettingByCompanyId",
    "createCompany",
    "updateCompany",
    "createSector",
    "retrieveCompaniesBySectorId",
    "updateSector",
    "updateCompany",
    "retrieveSectors",
    "retrieveSectorById",
    "retrieveCompanyById",
    "createUser",
    "updateUser",
    "retrieveUsersByCompanyId",
    "retrieveUserById",
    "retrieveUserByKeycloakId",
    "retrieveVisibleUsersByUserCompanyIdAndRoles",
    "retrieveUsersByManager",
    "sendActivationMail",
    "retrieveCompanyLogoUrlByCompanyId",
    "createCompanyLogoUrl",
    "saveCompanySetting",
    "retrieveCompanyByUserId",
    "retrieveCompanyLogoUrl",
    "retrieveCompaniesByQuery",
    "retrieveUsersByQuery",
    "retrieveUsersByIds",
    "retrieveProfilesByRoles",
    "retrieveProfilesByCompanyId",
    "retrieveProfilesByUserCompanyIdAndRoles",
    "retrieveProfilesByCompanyId",
    "retrieveProfilesForFunctionOrganogram",
    "retrieveProfilesByUserCompanyKeycloakIdAndRoles",
    "retrieveProfilesForSharingResult",
    "retrieveProfileByUserId",
    "retrieveProfileByUserKeycloakId",
    "updateProfile",
    "createDocumentUrl",
    "retrieveDocumentUrl",
    "removeUser",
    "createDepartment",
    "updateDepartment",
    "retrieveDepartmentsByCompanyId",
    "retrieveEligibleParentDepartments",
    "removeDepartment",
    "retrieveDepartmentById",
  ];

  private skillRouting = [
    "retrieveTasksBySectorId",
    "retrieveTaskById",
    "createTask",
    "updateTask",
    "retrieveTasksByQuery",
    "retrieveCompetencesBySectorId",
    "retrieveCompetenceById",
    "createCompetence",
    "updateCompetence",
    "retrieveCompetencesByQuery",
    "retrieveKnowledgeById",
    "retrieveKnowledgesBySectorId",
    "updateKnowledge",
    "createKnowledge",
    "retrieveKnowledgesByQuery",
    "retrieveCareerGroupsBySectorId",
    "retrieveCareerGroupById",
    "updateCareerGroup",
    "createCareerGroup",
    "retrieveCareerGroupsByQuery",
    "retrieveCareerProfilesBySectorId",
    "retrieveImportValidations",
    "importKnowledgeTopicData",
    "importCompetenceData",
    "importSkillData",
    "retrieveCareerProfileById",
    "createCareerProfile",
    "updateCareerProfile",
    "retrieveCareerProfilesBySectorIdAndCompanyId",
    "retrieveCareerProfilesByQuery",
    "retrieveTasksByIds",
    "retrieveSkillsByIds",
    "retrieveCompetencesByIds",
    "retrieveKnowledgeTopicsByIds",
    "removeCareerProfile",
    "duplicateCareerHouse",
    "retrieveCareerProfilesByIds",
    "retrieveCareerFunctionsByCompanyId",
    "retrieveCareerFunctionsByQuery",
    "createCareerFunction",
    "updateCareerFunction",
    "retrieveCareerFunctionById",
    "retrieveCareerTargets",
    "retrieveCareerTargetsByQuery",
    "retrieveCareerTargetsByIds",
    "createMatching",
    "createMatchingScoresByMatchingId",
    "retrieveMatchingScoresByMatchingId",
    "retrieveMatchingById",
    "retrieveAllMatchingsByCompanyId",
    "startMatching",
    "retrieveCareerFunctionsByIds",
    "retrieveSkillSetByUserId",
    "retrieveSkillSetById",
  ];

  private meetingRouting = [
    "createPop",
    "retrievePopById",
    "retrievePopsByEmployeeId",
    "retrieveAllPopsByEmployeeId",
    "updatePop",
    "retrievePmsByEmployeeId",
    "retrievePmById",
    "createPm",
    "updatePm",
    "retrieveActivePopByEmployeeId",
    "retrievePmsByEmployeeIdAndStatuses",
    "retrieveConversationLogsByPopId",
    "retrieveConversationLogsByPmId",
    "createConversationLog",
    "updateConversationLog",
    "retrieveConversationLogById",
    "createMeeting",
    "updateMeeting",
    "retrieveMeetingsByMonth",
    "retrieveMeetingById",
    "retrievePmReviewsByPmId",
    "createPmReview",
    "updatePmReview",
    "removePm",
    "removePop",
    "retrievePopFeedback",
    "retrievePmFeedback",
    "retrieveConversationLogsByEmployeeId",
    "approveConversationLog",
    "approvePm",
    "approvePop",
    "createPmFeedback",
    "createPopFeedback",
    "retrievePmStatusSummary",
    "retrievePopStatusSummary",
    "submitPmFeedbackRequest",
    "updatePmFeedbackExpiryDate",
    "submitPopFeedbackRequest",
    "updatePopFeedbackExpiryDate",
    "retrieveEmployeePerformanceOverview",
  ]

  private assessmentRouting = [
    "retrieveAssessments",
    "retrieveAssessmentsBySectorId",
    "retrieveFormbricksSurveys",
    "retrieveCompanyFormbricksSurveys",
    "retrieveGlobalAssessments",
    "retrieveAssessmentUserScopeContext",
    "sectors",
    "companies",
    "retrieveAssessmentLinksByEmployeeId",
    "retrieveAssessmentById",
    "startAssessmentDraftSession",
    "startAssessmentEditSession",
    "finalizeAssessmentEditorSession",
    "cancelAssessmentEditorSession",
    "updateAssessment",
    "retrieveAssessmentsByQuery",
    "retrieveAssessmentTestsByIds",
    "retrieveAssessmentByFormbricksId",
    "createAssessment",
    "createCompanyScopedAssessment",
    "updateAssessment",
    "shareAssessments",
    "shareResponses",
    "updateAssessmentResponseShares",
    "updateAliveScoreManagerShare",
    "retrieveAssessmentsByQuery",
    "retrieveAssessmentTestsByIds",
    "retrieveAssessmentLinkById",
    "retrieveSingleUseAssessmentLink",
    "retrieveAssessmentStatusSummary",
    "retrieveEmployeeAssessmentOverview",
  ];

  private notificationRouting = [
    "retrieveNotificationsByUserId",
    "markNotificationsAsRead",
    "retrieveEmployeeNotificationOverview",
  ];

  resolveUri(operationName?: string): string {
    if (!operationName) {
      return "graphql";
    }

    if (this.adminRouting.includes(operationName)) {
      return "admin/graphql";
    }

    if (this.skillRouting.includes(operationName)) {
      return "skills/graphql";
    }

    if (this.meetingRouting.includes(operationName)) {
      return "performance/graphql";
    }

    if (this.assessmentRouting.includes(operationName)) {
      return "assessments/graphql";
    }

    if (this.notificationRouting.includes(operationName)) {
      return "notifications/graphql";
    }

    return "graphql";
  }
}
